import { Injectable, inject } from '@angular/core';
import { catchError, tap, shareReplay } from 'rxjs/operators';
import { HttpParams, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AppGlobalsModule } from '../app-globals.module';
import { Observable, throwError, of } from 'rxjs';
import { ErrorsHandler } from '../core/errors/errors-handler/errors-handler';
import { UserDataService, UserActionType } from './user-data.service';
import { YeshivaModels } from 'src/serverTypes/yeshivaTypesDeclare';
import { I18nService } from '../core/services/i18n/i18n.service';

@Injectable({
    providedIn: 'root',
})
export class UserSeriesService {
    protected http = inject(HttpClient);
    protected _i18n = inject(I18nService);
    protected userDataService = inject(UserDataService);
    protected errorsHandler = inject(ErrorsHandler);

    public readonly SERIES_FIELD = 'registeredMidrashSerieses';
    public readonly SERIES_LIMIT = 20;
    //TODO: This is hughly unoptimized, we should change it tp maps, but carfully because the DB saves only arrays.

    public registeredSerieses: Map<number, number[]> | null = null; // <Id, viewed>
    private seriesFieldUrl: string;
    private userSeries: string;

    constructor() {
        const _i18n = this._i18n;

        this.seriesFieldUrl = AppGlobalsModule.warehousePath('userField', _i18n.getLang());
        this.userSeries = AppGlobalsModule.warehousePath('userSeries', _i18n.getLang());
    }

    public updateRegistration(
        seriesEntity: YeshivaModels.BE.Series,
        currectContent: number,
        toRegister: boolean,
    ): Observable<Map<number, number[]>> | Observable<never> {
        // on slow loading, don't allow user to ovveride the series
        if (!this.registeredSerieses) {
            return throwError('You must initiate series before anything');
        }

        // Register series
        if (toRegister) {
            // Maximum registration series at a time is 20
            if (this.registeredSerieses.size >= this.SERIES_LIMIT) {
                return throwError({
                    message: 'Series subscriptions limit exceeded',
                    showUser: true,
                    i18n: 'tooManySerieses',
                });
            }

            if (this.isRegisteredSync(seriesEntity.Id)) {
                return of(this.registeredSerieses);
            }

            //TODO: user user-data service to register to series

            // Save serires
            let params = new HttpParams();
            params = params.set('field', this.SERIES_FIELD);
            params = params.set('seriesMidrashId', seriesEntity.Id.toString());
            params = params.set('contentId', currectContent.toString());
            let options = { withCredentials: true, params };

            return this.http.post(this.userSeries + '?', options).pipe(
                catchError<any, Observable<never>>((error) => {
                    this.errorsHandler.handleError(error);
                    return throwError(error);
                }),
                tap((data) => {
                    // Add to local cache
                    this.registeredSerieses?.set(seriesEntity.Id, [currectContent]);
                }),
                tap(() => this.userDataService.updateUserData()),
            );
        } else {
            // Unregister series
            let params = new HttpParams();
            params = params.set('field', this.SERIES_FIELD);
            params = params.set('seriesMidrashId', seriesEntity.Id.toString());

            return this.http.delete(this.userSeries + '?', { params, withCredentials: true }).pipe(
                catchError<any, Observable<never>>((error: HttpErrorResponse) => {
                    this.errorsHandler.handleError(error);
                    return throwError(error);
                }),
                tap((data) => {
                    // Remove from local cache
                    let tempseries = this.registeredSerieses?.has(seriesEntity.Id);
                    if (tempseries) {
                        this.registeredSerieses?.delete(seriesEntity.Id);
                    }
                }),
                tap(() => this.userDataService.updateUserData()),
            );
        }
    }

    /**
     * mark / unmark view of content in series.
     * @param seriesId
     * @param contentid
     * @param toMark
     */
    public markContentView(seriesId: number, contentid: number, toMark: boolean): Observable<any> | Observable<never> {
        // If we already registered we do not need to try to register again
        if (toMark && this.isMarkedView(seriesId, contentid)) {
            return of(true);
        }

        let params = new HttpParams();
        params = params
            .set('contentid', '' + contentid)
            .set('toRegister', '' + toMark)
            .set(this.SERIES_FIELD, seriesId.toString());
        let viewed = this.registeredSerieses?.get(seriesId) ?? [];
        let options = { withCredentials: true, params };

        return this.http.put<Map<number, number[]>>(this.userSeries + '?', {}, options).pipe(
            tap((x) => {
                if (toMark) {
                    viewed.push(contentid);
                } else {
                    viewed.splice(viewed.indexOf(contentid), 1);
                }
            }),
            tap(() => this.userDataService.updateUserData()),
            catchError((error) => {
                this.errorsHandler.handleError(error);
                return throwError(error);
            }),
        );
    }
    public isMarkedView(seriesId: number, contentId: number): boolean {
        if (!this.registeredSerieses) {
            return false;
        }

        let series = this.registeredSerieses.has(seriesId);
        return series && this.registeredSerieses.get(seriesId)?.find((x) => x === contentId) !== undefined;
    }

    public async getAll(): Promise<Map<number, number[]> | null> {
        // TODO: fix signature
        await this.initiateRegisteredSeries();
        // Check if the content if favorited.
        return this.registeredSerieses;
    }

    public async isRegistered(seriesId: number): Promise<boolean> {
        await this.initiateRegisteredSeries();
        // Check if the content if favorited.
        return this.isRegisteredSync(seriesId);
    }

    private isRegisteredSync(seriesId: number): boolean {
        return this.registeredSerieses?.has(seriesId) ?? false;
    }

    private async initiateRegisteredSeries() {
        // If data initiated we don't need to initiate again.
        if (this.registeredSerieses) {
            return;
        }

        return this.userDataService
            .getDataField(UserActionType.Series)
            .pipe(
                shareReplay({ bufferSize: 1, refCount: true }),
                catchError((error) => {
                    // If user is Unauthorized, show error to user.
                    if (error.status === 401) {
                        return throwError({
                            showUser: true,
                            message: `We couldn't fetch your data, please login and try again.`,
                            i18n: 'unauthorizedUserFetch',
                            data: {
                                message: `We couldn't fetch your data, please login and try again.`,
                                i18n: 'unauthorizedUserFetch',
                            },
                        });
                    }
                    this.errorsHandler.handleError(error);
                    return throwError(error);
                }),
                tap((data) => {
                    // If the user has never registered to anything it would still be null, thuse keep it empty
                    this.registeredSerieses = new Map<number, number[]>();
                    if (data) {
                        for (const serie of data) {
                            this.registeredSerieses.set(serie.Id, serie.viewed);
                        }
                    }
                }),
            )
            .toPromise();
    }
}
